/**
 * Batch upload materials modal
 */

import React, { useState, useEffect } from 'react';
import { Modal, Form, Select, TreeSelect, Upload, message, Progress } from 'antd';
import { InboxOutlined } from '@ant-design/icons';
import { materialApi, folderApi } from '../../services/resourceService';
import { ResourceScope, ResourceFolder, FolderType } from '../../types/resource';

const { Option } = Select;
const { Dragger } = Upload;

interface BatchUploadModalProps {
  visible: boolean;
  scope: ResourceScope;
  onCancel: () => void;
  onSuccess: () => void;
}

const BatchUploadModal: React.FC<BatchUploadModalProps> = ({
  visible,
  scope,
  onCancel,
  onSuccess,
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [folders, setFolders] = useState<ResourceFolder[]>([]);
  const [fileList, setFileList] = useState<any[]>([]);
  const [uploadProgress, setUploadProgress] = useState(0);

  // Load folders
  useEffect(() => {
    if (visible) {
      loadFolders();
    }
  }, [visible, scope]);

  const loadFolders = async () => {
    try {
      const result = await folderApi.getTree(FolderType.MATERIAL, scope);
      setFolders(result);
    } catch (error) {
      console.error('Failed to load folders:', error);
    }
  };

  // Convert folders to tree data
  const buildTreeData = (folders: ResourceFolder[]): any[] => {
    return folders.map((folder) => ({
      title: folder.name,
      value: folder.id,
      children: folder.children ? buildTreeData(folder.children) : [],
    }));
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      
      if (fileList.length === 0) {
        message.error('请选择要上传的文件');
        return;
      }

      setLoading(true);
      setUploadProgress(0);

      // Extract File objects from fileList
      // When beforeUpload returns false, the file itself is stored in fileList
      const files = fileList.map(item => item.originFileObj || item);

      // Batch upload
      const results = await materialApi.uploadBatch(files, {
        scope: scope,
        folder_id: values.folder_id || undefined,
      });

      setUploadProgress(100);
      message.success(`成功上传 ${results.length} 个文件`);
      form.resetFields();
      setFileList([]);
      onSuccess();
    } catch (error: any) {
      if (error.errorFields) {
        return;
      }
      console.error('批量上传失败:', error);
      message.error(error.response?.data?.detail || error.message || '上传失败');
    } finally {
      setLoading(false);
      setUploadProgress(0);
    }
  };

  const uploadProps = {
    name: 'file',
    multiple: true,
    fileList,
    beforeUpload: (file: any) => {
      setFileList(prev => [...prev, file]);
      return false; // Prevent auto upload
    },
    onRemove: (file: any) => {
      setFileList(prev => prev.filter(item => item.uid !== file.uid));
    },
  };

  return (
    <Modal
      title="批量上传素材"
      open={visible}
      onOk={handleSubmit}
      onCancel={onCancel}
      confirmLoading={loading}
      width={600}
      destroyOnClose
    >
      <Form
        form={form}
        layout="vertical"
      >
        <Form.Item label="文件">
          <Dragger {...uploadProps}>
            <p className="ant-upload-drag-icon">
              <InboxOutlined />
            </p>
            <p className="ant-upload-text">点击或拖拽文件到此区域上传</p>
            <p className="ant-upload-hint">
              支持批量上传，可同时选择多个文件
            </p>
          </Dragger>
        </Form.Item>

        {loading && uploadProgress > 0 && (
          <Form.Item>
            <Progress percent={uploadProgress} status="active" />
          </Form.Item>
        )}

        <Form.Item name="folder_id" label="分组">
          <TreeSelect
            placeholder="请选择分组（可选）"
            allowClear
            treeData={buildTreeData(folders)}
          />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default BatchUploadModal;
